import '../eldgard.css';
import '../eldgard-legal.css';

import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  FileText,
  Mail,
  ShieldCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { WebsiteAnalyticsNotice } from '../components/WebsiteAnalyticsNotice';

export type EldgardDocument = 'privacy' | 'terms' | 'support' | 'data-deletion';

type DocumentLink = {
  key: EldgardDocument;
  title: string;
  href: string;
  description: string;
};

type TocItem = {
  href: string;
  label: string;
};

const documentLinks: DocumentLink[] = [
  {
    key: 'privacy',
    title: 'Privacy Policy',
    href: '/eldgard/privacy',
    description:
      'What the desktop and mobile apps access, where it goes, and what stays on your device.',
  },
  {
    key: 'terms',
    title: 'Terms of Use',
    href: '/eldgard/terms',
    description:
      'The rules, responsibilities, and current limitations that apply when you use Eldgard.',
  },
  {
    key: 'support',
    title: 'Support',
    href: '/eldgard/support',
    description:
      'Practical help for Google sign-in, IAM, emulators, imports, exports, and pre-release builds.',
  },
  {
    key: 'data-deletion',
    title: 'Data & Deletion',
    href: '/eldgard/data-deletion',
    description:
      'How to disconnect Google, remove local app records, revoke keys, and delete support data.',
  },
];

const effectiveDate = <time dateTime="2026-09-18">September 18, 2026</time>;

function FlameMark() {
  return <span className="eldgard-flame" aria-hidden="true" />;
}

function DocumentNavigation({ current }: { current?: EldgardDocument }) {
  return (
    <nav className="eldgard-document-nav" aria-label="Eldgard documents">
      <a href="/eldgard/docs" aria-current={!current ? 'page' : undefined}>
        All documents
      </a>
      {documentLinks.map((item) => (
        <a
          href={item.href}
          key={item.key}
          aria-current={current === item.key ? 'page' : undefined}
        >
          {item.title}
        </a>
      ))}
    </nav>
  );
}

function EldgardDocumentShell({ children }: { children: ReactNode }) {
  return (
    <div className="eldgard-site eldgard-document-site">
      <a className="eldgard-skip" href="#content">
        Skip to content
      </a>
      <header className="eldgard-header eld-container">
        <a className="eldgard-brand" href="/eldgard" aria-label="Eldgard home">
          <FlameMark />
          <span>Eldgard</span>
        </a>
        <nav
          className="eldgard-legal-top-nav"
          aria-label="Eldgard legal navigation"
        >
          <a href="/eldgard">
            <ArrowLeft size={13} aria-hidden="true" /> Product
          </a>
          <a href="/eldgard/docs">Documents</a>
          <a href="/eldgard/support">Support</a>
        </nav>
        <a
          className="eldgard-studio-link"
          href="/"
          aria-label="Back to alxnsv.io"
        >
          <ArrowLeft size={13} aria-hidden="true" /> alxnsv.io
        </a>
      </header>

      {children}

      <footer className="eldgard-footer eldgard-document-footer eld-container">
        <a className="eldgard-brand" href="/eldgard">
          <FlameMark />
          <span>Eldgard</span>
        </a>
        <nav aria-label="Eldgard footer documents">
          <a href="/eldgard/privacy">Privacy</a>
          <a href="/eldgard/terms">Terms</a>
          <a href="/eldgard/support">Support</a>
          <a href="/eldgard/data-deletion">Data &amp; Deletion</a>
        </nav>
        <a href="/">
          Made by alxnsv.io <ArrowRight size={14} aria-hidden="true" />
        </a>
      </footer>
    </div>
  );
}

function DocumentPage({
  current,
  eyebrow,
  title,
  introduction,
  toc,
  children,
}: {
  current: EldgardDocument;
  eyebrow: string;
  title: string;
  introduction: ReactNode;
  toc: TocItem[];
  children: ReactNode;
}) {
  return (
    <EldgardDocumentShell>
      <main id="content" className="eldgard-legal-main" tabIndex={-1}>
        <header className="eldgard-legal-hero eld-container">
          <DocumentNavigation current={current} />
          <div className="eldgard-legal-hero-grid">
            <div>
              <p className="eldgard-eyebrow">{eyebrow}</p>
              <h1>{title}</h1>
            </div>
            <div className="eldgard-legal-intro">
              <div>{introduction}</div>
              <p>Effective and last updated {effectiveDate}</p>
            </div>
          </div>
        </header>

        <div className="eldgard-document-layout eld-container">
          <aside className="eldgard-document-toc">
            <p>On this page</p>
            <nav aria-label={`On this ${title} page`}>
              {toc.map((item) => (
                <a href={item.href} key={item.href}>
                  {item.label}
                </a>
              ))}
            </nav>
          </aside>
          <article className="eldgard-document-body">{children}</article>
        </div>
      </main>
    </EldgardDocumentShell>
  );
}

function PrivacyPolicy() {
  return (
    <DocumentPage
      current="privacy"
      eyebrow="A considered boundary"
      title="Privacy Policy"
      introduction={
        <p>
          What Eldgard accesses, why it needs that access, and how you stay in
          control of your data.
        </p>
      }
      toc={[
        { href: '#privacy-summary', label: 'The short version' },
        { href: '#privacy-scope', label: 'Scope' },
        { href: '#privacy-google-data', label: 'Google user data' },
        { href: '#privacy-local-data', label: 'Local app data' },
        { href: '#privacy-use-sharing', label: 'Use and sharing' },
        { href: '#privacy-retention', label: 'Retention and deletion' },
        { href: '#privacy-security', label: 'Security and choices' },
        { href: '#website-analytics', label: 'Website analytics' },
        { href: '#privacy-rights', label: 'Privacy rights' },
        { href: '#privacy-contact', label: 'Contact' },
      ]}
    >
      <section id="privacy-summary">
        <h2>The short version</h2>
        <p>
          Eldgard is a Firestore client, not a data host. The apps send requests
          directly from your device to Google APIs or to the Firestore emulator
          endpoint you choose. There is no Eldgard-operated proxy receiving your
          Firestore documents, and the current apps have no advertising or
          behavioral analytics. We do not sell personal information or Google
          user data.
        </p>
        <p>
          Desktop Google refresh tokens are stored in your operating system’s
          credential vault. On Android and iOS, Google’s native sign-in software
          manages authentication persistence. App preferences, connection
          details, saved queries, and workflow metadata are stored locally as
          described below.
        </p>
      </section>

      <section id="privacy-scope">
        <h2>Scope</h2>
        <p>
          This policy covers the Eldgard desktop app for macOS, Windows, and
          Linux; the read-only Eldgard mobile app for Android and iOS; the
          Eldgard pages on this website; early-access requests; and support
          messages sent to alxnsv.io.
          Eldgard is developed by Aleksandr Nosov under the alxnsv.io name.
        </p>
        <p>
          Google, Firebase, your organization, your device platform, and any
          emulator host you connect to handle information under their own
          policies and agreements. If an employer or another organization
          manages your Google Cloud account or Firestore project, that
          organization may also control and audit your access.
        </p>
      </section>

      <section id="privacy-google-data">
        <h2>Google user data Eldgard accesses</h2>
        <p>
          When you choose Google sign-in, Eldgard may access the following data
          for the account you select:
        </p>
        <ul>
          <li>
            <strong>Account identity:</strong> your Google account identifier
            and email address, plus your display name on mobile, to show which
            account is connected and keep accounts separate.
          </li>
          <li>
            <strong>Firebase and Cloud metadata:</strong> projects visible to
            your account and their Firestore database metadata, so you can pick
            a project and database.
          </li>
          <li>
            <strong>Firestore data:</strong> collection paths, document IDs,
            document fields and values, query results, and related Firestore
            responses needed to browse and work with the database you select.
          </li>
          <li>
            <strong>Authorization data:</strong> short-lived access tokens and,
            on desktop, a refresh token used to keep you signed in. Eldgard does
            not receive your Google password.
          </li>
        </ul>
        <h3>Permissions requested</h3>
        <p>Depending on platform and the step you choose, Eldgard requests:</p>
        <ul>
          <li>
            <code>openid</code> and Google’s email/profile access for the
            account identifier and display information;
          </li>
          <li>
            <code>https://www.googleapis.com/auth/firebase.readonly</code> to
            discover available Firebase projects; and
          </li>
          <li>
            <code>https://www.googleapis.com/auth/datastore</code> to call the
            Firestore API.
          </li>
        </ul>
        <p>
          The desktop app can read and modify Firestore data. The mobile app is
          designed as a read-only client, but Google does not provide a
          read-only Firestore OAuth scope; the Datastore scope itself can
          authorize broader access. Your Google Cloud IAM roles remain the
          controlling boundary, and Google sign-in does not grant a new project
          role.
        </p>
        <p>
          Use an account and IAM role limited to the access you intend. For the
          mobile reader, use a read-only IAM role when connecting to production
          data. Requests authenticated with Google OAuth or a service account
          are governed by Google Cloud IAM, not Firebase client Security Rules.
        </p>
      </section>

      <section id="privacy-local-data">
        <h2>Data stored or handled on your device</h2>
        <h3>Desktop</h3>
        <ul>
          <li>
            The secure session record contains your Google account identifier,
            email address, selected project IDs, refresh token, and an
            authorization-version marker. It is stored through the operating
            system credential vault.
          </li>
          <li>
            Saved query definitions and the 15 most recent query definitions are
            stored in <code>~/.fireground/queries.json</code>. Query values may
            themselves contain personal or confidential information.
          </li>
          <li>
            Autoflow definitions are stored in{' '}
            <code>~/.fireground/flows.json</code>. They can include project and
            database identifiers, collection names, a local seed-file path, and
            last-run status. The source file is not copied into that record.
          </li>
          <li>
            The activity log is a bounded, in-memory view of recent actions and
            errors. It is not an analytics feed or a persistent log file.
          </li>
          <li>
            Workspace recovery keeps tab references, query editor state, and
            view settings in operating-system secure storage, with a backup
            snapshot. Optional document-draft retention is off by default. When
            enabled, it can retain document contents, unfinished edits, and
            original server versions. Recovery has size limits and a seven-day
            tab retention window; it does not persist fetched pages as an
            offline database.
          </li>
          <li>
            The optional local mutation journal is separate from the activity
            log. Recording is off by default. When enabled for a project scope,
            it stores reviewed single-document operations, typed before/after
            contents, resource paths, times, server versions, and outcomes in
            secure storage. Its default retention is 30 days, subject to entry
            and size limits. Pending operations and active restore records may
            be retained until settled. It keeps one additional secure backup
            snapshot and has no plaintext fallback.
          </li>
          <li>
            Read-only protection settings are stored locally in{' '}
            <code>~/.fireground/read_only.json</code>. Secure credential,
            recovery, and journal storage does not make every local app file
            encrypted.
          </li>
          <li>
            A service-account JSON key you select is read into app memory to
            create that connection. Eldgard does not copy the key into its
            settings or session files. The original key file remains wherever
            you stored it.
          </li>
        </ul>
        <h3>Mobile</h3>
        <ul>
          <li>
            Saved local-emulator profiles can include a name, host, port,
            project ID, database ID, and the last selected profile. They are
            stored in the app’s application-support directory.
          </li>
          <li>
            Saved query definitions are stored locally per database and query
            scope, including collection groups, and can contain field names and
            query values.
          </li>
          <li>
            Home stores up to 20 pins, 30 recent places, and 20 remembered cloud
            destinations in a bounded local file. These records contain
            endpoint, account, project, database, path, and saved-query
            identifiers—not document bodies, query results, or tokens.
            Document-change comparisons and frozen baselines stay in memory and
            are not persisted.
          </li>
          <li>
            Google’s native sign-in SDK manages the authorized account and token
            persistence. Eldgard does not write access tokens into mobile
            connection profiles.
          </li>
        </ul>
        <h3>Firestore content and files</h3>
        <p>
          Firestore content is held in memory while you view or edit it and can
          be retained by the optional desktop recovery and journal features
          described above. Desktop JSON and CSV imports may use temporary local
          staging files; exports create files at the location you choose. Copy
          actions and saved-query handoff put selected content on the system
          clipboard. Handoff includes query names, scope, field paths, and
          operand values, but not credentials or result documents. You choose
          how to transfer that text; the transfer channel, exported files, and
          clipboard history are outside Eldgard’s control.
        </p>
      </section>

      <section id="privacy-use-sharing">
        <h2>How data is used and shared</h2>
        <p>
          Eldgard uses Google user data only to authenticate you, discover the
          projects and databases available to your account, display and query
          Firestore data, and perform the user-directed desktop operation you
          choose. We do not use it for advertising, credit decisions, data
          brokerage, or training general-purpose AI models.
        </p>
        <p>
          The app transmits data to Google when calling Google and Firebase APIs
          and to a local or network emulator host when you choose that endpoint.
          We do not transfer Google user data to third parties except when you
          direct the app to an endpoint, file, or clipboard destination, or when
          disclosure is required by law. If you send logs, screenshots, or data
          to support, that material is shared with the email providers used to
          deliver and store the conversation.
        </p>
        <p>
          If you request early access, we use the email address you submit to
          manage your request and contact you about Eldgard invitations and
          launch availability. Formspree processes and stores the submission
          and forwards it to our email inbox. Our email providers process and
          store the forwarded notification. Formspree also processes request
          and security information under its{' '}
          <a href="https://formspree.io/legal/privacy-policy/" target="_blank" rel="noreferrer">
            Privacy Policy<span className="sr-only"> (opens in a new tab)</span>
          </a>.
        </p>
        <p>
          Eldgard’s use and transfer of information received from Google APIs
          adheres to the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          , including the Limited Use requirements.
        </p>
      </section>

      <section id="privacy-retention">
        <h2>Retention and deletion</h2>
        <p>
          Local settings remain until you remove them or clear the app’s data. A
          desktop Google session remains in secure storage until its projects
          are removed from Eldgard, the session becomes invalid, or the secure
          record is otherwise deleted. Google’s mobile SDK may retain the
          authorized account until you sign out, revoke access, or clear the
          app’s data.
        </p>
        <p>
          Secure workspace recovery and local mutation history have the
          retention limits described above and separate in-app clearing
          controls. Disconnecting Google does not itself clear those records,
          saved queries, or files you exported. Complete the applicable steps on
          the Data &amp; Deletion page for a full local cleanup.
        </p>
        <p>
          Support correspondence is kept only as long as reasonably needed to
          answer the request, maintain support records, resolve disputes, or
          meet legal and security obligations. Website infrastructure may keep
          limited request and security logs according to the hosting provider’s
          operational retention settings.
        </p>
        <p>
          Our current Formspree plan provides 30 days of submission history in
          its dashboard. We retain forwarded requests in our email inbox while
          managing early-access invitations and launch communication, unless
          you ask us to delete them sooner. The dashboard history limit does
          not delete inbox copies. You can withdraw your request by emailing
          hello@alxnsv.io from the address you submitted.
        </p>
        <p>
          See <a href="/eldgard/data-deletion">Data &amp; Deletion</a> for
          step-by-step removal instructions. Removing a connection or deleting
          Eldgard does not delete documents from Firestore, delete a Google
          Cloud project, remove an IAM role, erase an exported file, or revoke a
          service-account key.
        </p>
      </section>

      <section id="privacy-security">
        <h2>Security and your choices</h2>
        <p>
          Eldgard uses Google’s installed-app sign-in flow on desktop, native
          Google sign-in on mobile, TLS for production Google API requests, and
          the operating system credential store for desktop refresh tokens. No
          method of storage or transmission is completely secure, so protect
          your device, local files, emulator network, and credentials.
        </p>
        <p>
          You can use a local emulator without Google sign-in, decline Google
          access, remove saved connections, sign out on mobile, revoke Eldgard
          in your Google Account, or stop using the app. Restrict emulator
          access to trusted networks: an emulator exposed on a LAN may be
          reachable by other devices and should not be treated as a production
          security boundary.
        </p>
      </section>

      <WebsiteAnalyticsNotice />

      <section id="privacy-rights">
        <h2>Privacy rights</h2>
        <p>
          Depending on where you live, you may have rights to ask about, access,
          correct, delete, restrict, or object to certain processing of your
          personal information, or to receive a portable copy. Eldgard cannot
          retrieve app data that never leaves your device or Google project; use
          the app, device, Google Account, and project controls described in{' '}
          <a href="/eldgard/data-deletion">Data &amp; Deletion</a>
          for that material. Contact us about support correspondence or another
          record held by alxnsv.io. We may need to verify the request and may
          retain information where the law permits or requires it.
        </p>
        <p>
          Eldgard is a professional developer tool and is not directed to
          children under 13 or the higher minimum age required where they live.
          We do not knowingly solicit children’s personal information. If you
          believe a child sent personal information to support, contact us so we
          can review and delete it where required.
        </p>
      </section>

      <section id="privacy-contact">
        <h2>Changes and contact</h2>
        <p>
          We may update this policy as Eldgard changes. The effective date above
          identifies the current version. Material changes will be reflected on
          this page before or when they take effect. Paid checkout, license
          activation, and public trials are not currently active; this policy
          will be updated before a launch introduces additional purchase or
          activation data handling.
        </p>
        <p>
          For a privacy question or a request involving early-access or support
          data, email{' '}
          <a href="mailto:hello@alxnsv.io?subject=Eldgard%20privacy">
            hello@alxnsv.io
          </a>
          . Include no Google password, OAuth token, service-account private
          key, or unnecessary Firestore content.
        </p>
      </section>
    </DocumentPage>
  );
}

function TermsOfUse() {
  return (
    <DocumentPage
      current="terms"
      eyebrow="Terms for the trail"
      title="Terms of Use"
      introduction={
        <p>
          The practical agreement for using Eldgard responsibly with systems and
          data you are authorized to access.
        </p>
      }
      toc={[
        { href: '#terms-agreement', label: 'Agreement and scope' },
        { href: '#terms-license', label: 'License and use' },
        { href: '#terms-responsibility', label: 'Your responsibilities' },
        { href: '#terms-data', label: 'Data and credentials' },
        { href: '#terms-third-parties', label: 'Third-party services' },
        { href: '#terms-prerelease', label: 'Pre-release software' },
        { href: '#terms-payment', label: 'Planned paid plans' },
        { href: '#terms-liability', label: 'Warranty and liability' },
        { href: '#terms-changes', label: 'Changes and contact' },
      ]}
    >
      <section id="terms-agreement">
        <h2>Agreement and scope</h2>
        <p>
          These Terms of Use apply when you access or use Eldgard’s desktop or
          mobile software, website, documentation, or pre-release builds. By
          using Eldgard, you agree to these terms and the{' '}
          <a href="/eldgard/privacy">Privacy Policy</a>. If you use Eldgard for
          an organization, you confirm that you are authorized to accept these
          terms for that organization.
        </p>
        <p>
          You must be legally able to enter this agreement. Do not use Eldgard
          where its use would violate applicable law, contract, workplace
          policy, or a restriction placed on the data or system you access.
        </p>
      </section>

      <section id="terms-license">
        <h2>License and acceptable use</h2>
        <p>
          Subject to these terms and any order or license terms supplied with a
          release, alxnsv.io grants you a limited, non-exclusive,
          non-transferable, revocable right to install and use Eldgard for
          lawful personal or internal business work.
        </p>
        <p>You may not:</p>
        <ul>
          <li>use Eldgard to access data or systems without authorization;</li>
          <li>
            distribute, resell, sublicense, rent, or share a license beyond the
            users and devices it covers;
          </li>
          <li>
            bypass licensing, authentication, rate limits, or technical
            safeguards;
          </li>
          <li>
            interfere with Eldgard, Google services, or another user’s systems;
            or
          </li>
          <li>
            reverse engineer the app except to the extent applicable law
            expressly permits it despite this restriction.
          </li>
        </ul>
      </section>

      <section id="terms-responsibility">
        <h2>Your responsibilities</h2>
        <p>
          You are responsible for choosing the correct project, database,
          collection, query, and destination before acting. The desktop app can
          create, update, and delete Firestore data and can import many records
          at once. Review important changes, keep suitable backups, and test
          automation or bulk work against an emulator or non-production project
          first.
        </p>
        <p>
          You are also responsible for your device security, network, emulator
          exposure, Google Cloud configuration, IAM roles, API usage and
          charges, service-account lifecycle, exported files, and compliance
          with obligations that apply to your data. A read-only Eldgard mobile
          interface is not a substitute for a read-only IAM role.
        </p>
      </section>

      <section id="terms-data">
        <h2>Your data and credentials</h2>
        <p>
          You retain your rights in the Firestore content, query values, local
          files, and other information you use with Eldgard. You authorize
          Eldgard to process that material on your device and send it to the
          endpoint you select only as needed to perform your requested action.
        </p>
        <p>
          Keep OAuth access, service-account keys, exported JSON, and emulator
          endpoints confidential. Use narrowly scoped accounts and rotate or
          revoke credentials you believe may be exposed. Never send credentials
          to support. Removing a connection from Eldgard does not remove IAM
          roles, delete cloud data, or invalidate a service-account key.
        </p>
      </section>

      <section id="terms-third-parties">
        <h2>Google and other third-party services</h2>
        <p>
          Eldgard depends on services and software provided by others, including
          Google Cloud, Firebase, Google sign-in, operating-system credential
          stores, app distribution platforms, and open-source components. Your
          use of those services is subject to their own terms, policies, quotas,
          fees, and availability. Eldgard cannot grant Google Cloud access or
          override IAM decisions.
        </p>
        <p>
          Eldgard is an independent product by alxnsv.io. It is not affiliated
          with or endorsed by Google or Firebase. Google, Firebase, Firestore,
          and their related marks belong to Google.
        </p>
      </section>

      <section id="terms-prerelease">
        <h2>Pre-release software</h2>
        <p>
          Eldgard is currently in development. Preview and early-access builds
          may be incomplete, change without notice, contain defects, require
          reauthorization, or stop working with future operating-system or
          Google API changes. Do not rely on a pre-release build as the sole
          tool for critical production work.
        </p>
        <p>
          Feedback is voluntary. If you provide it, you allow alxnsv.io to use
          it without restriction or compensation to improve Eldgard, provided
          that this does not transfer ownership of your confidential data or
          override the Privacy Policy.
        </p>
      </section>

      <section id="terms-payment">
        <h2>Planned paid plans</h2>
        <p>
          Monthly and yearly subscription pricing, trials, device limits,
          included updates, and support shown on the product page are plans for a
          future launch, may change, and are not currently an offer to sell. Purchases
          and trials are not open. When sales begin, the checkout and license
          terms presented with the order will state the final price, taxes,
          renewal, cancellation, refund, activation, and usage rights and will
          control if they conflict with a product preview.
        </p>
      </section>

      <section id="terms-liability">
        <h2>Warranty and liability</h2>
        <p>
          To the fullest extent permitted by law, Eldgard is provided “as is”
          and “as available,” without warranties of uninterrupted operation,
          accuracy, fitness for a particular purpose, non-infringement, or
          compatibility with every database, emulator, device, API, or future
          platform release.
        </p>
        <p>
          To the fullest extent permitted by law, Aleksandr Nosov and alxnsv.io
          are not liable for indirect, incidental, special, exemplary, or
          consequential damages; loss or corruption of data; lost revenue; lost
          opportunity; or unauthorized access arising from use of Eldgard. If
          liability cannot be excluded, total liability is limited to the amount
          you paid for Eldgard in the 12 months before the event giving rise to
          the claim. Rights and remedies that cannot legally be waived remain in
          effect.
        </p>
      </section>

      <section id="terms-changes">
        <h2>Suspension, changes, and contact</h2>
        <p>
          Your permission to use Eldgard ends if you materially breach these
          terms. We may change or discontinue pre-release features and may
          update these terms for future use. If you do not agree to an update,
          stop using Eldgard. If one provision is unenforceable, the others
          remain in effect. A failure to enforce a provision is not a waiver.
        </p>
        <p>
          Questions can be sent to{' '}
          <a href="mailto:hello@alxnsv.io?subject=Eldgard%20terms">
            hello@alxnsv.io
          </a>
          .
        </p>
      </section>
    </DocumentPage>
  );
}

function SupportPage() {
  return (
    <DocumentPage
      current="support"
      eyebrow="A clear way home"
      title="Support"
      introduction={
        <>
          <p>
            Help goes directly to the independent developer building Eldgard.
          </p>
          <a
            className="eldgard-document-primary-action"
            href="mailto:hello@alxnsv.io?subject=Eldgard%20support"
          >
            <Mail size={17} aria-hidden="true" /> Email support
            <ArrowUpRight size={15} aria-hidden="true" />
          </a>
        </>
      }
      toc={[
        { href: '#support-contact', label: 'Contact support' },
        { href: '#support-sign-in', label: 'Google sign-in and IAM' },
        { href: '#support-emulator', label: 'Local emulator' },
        { href: '#support-data', label: 'Imports and data changes' },
        { href: '#support-protection', label: 'Safeguards and recovery' },
        { href: '#support-mobile', label: 'Mobile reader' },
        { href: '#support-handoff', label: 'Saved-query handoff' },
        { href: '#support-security', label: 'Security reports' },
      ]}
    >
      <section id="support-contact">
        <h2>Contact support</h2>
        <p>
          Email{' '}
          <a href="mailto:hello@alxnsv.io?subject=Eldgard%20support">
            hello@alxnsv.io
          </a>
          . Eldgard is still in development, so support is best-effort and no
          response-time guarantee currently applies.
        </p>
        <p>Please include:</p>
        <ul>
          <li>desktop or mobile, operating system, and OS version;</li>
          <li>Eldgard version or build identifier;</li>
          <li>whether the target is Google Cloud or a local emulator;</li>
          <li>the action you tried and the complete error message; and</li>
          <li>steps that reproduce the problem.</li>
        </ul>
        <p>
          Screenshots are useful after you crop or cover account email, project
          identifiers, document data, and private paths. Never send a password,
          OAuth token, service-account JSON, private key, exported database,
          secret environment file, or unredacted production record.
        </p>
      </section>

      <section id="support-sign-in">
        <h2>Google sign-in and IAM</h2>
        <p>
          Signing in identifies your Google account but does not grant that
          account a new role. If a project or database is missing, confirm that
          the chosen account can see the Firebase project and that its Google
          Cloud IAM role authorizes the operation you are attempting.
        </p>
        <p>
          A permission error from a production database is normally an IAM
          issue. Firebase client Security Rules do not authorize Google OAuth or
          service-account REST requests. For the mobile reader, assign a
          read-only IAM role even though the interface itself contains no write
          controls.
        </p>
        <p>
          If a saved sign-in stops working, remove the affected cloud project,
          reconnect the Google account, and approve the scopes again. You can
          also review or revoke Eldgard from{' '}
          <a
            href="https://myaccount.google.com/connections"
            target="_blank"
            rel="noreferrer"
          >
            your Google Account connections
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          .
        </p>
      </section>

      <section id="support-emulator">
        <h2>Local emulator connections</h2>
        <p>
          On the same desktop, start with <code>127.0.0.1</code> and the
          Firestore emulator port, commonly <code>8080</code>. Confirm the
          project ID matches the emulator configuration and that the emulator is
          already running.
        </p>
        <p>
          A physical phone cannot use its own <code>127.0.0.1</code> to reach an
          emulator running on your computer. Use the computer’s reachable LAN
          address, allow the port through the firewall, keep both devices on a
          trusted network, and avoid exposing the emulator to the public
          internet. Network and emulator behavior vary by development setup.
        </p>
      </section>

      <section id="support-data">
        <h2>Before imports, deletes, and bulk changes</h2>
        <p>
          Confirm the selected project, database, and collection and make a
          backup before a desktop import or destructive action. Test the same
          workflow in an emulator when possible. CSV imports validate every row
          and only create new documents; existing IDs are rejected. Other import
          formats have different behavior, and Firefoo imports can overwrite
          fields on existing documents. Check the chosen import policy. A
          successful operation changes the connected database, not merely the
          local view. Interrupted or failed bulk work can leave partial changes;
          inspect the reported outcome before retrying.
        </p>
        <p>
          Exports are written to the path you select. If an export is missing,
          check that location and your operating system’s file permissions. An
          Autoflow records the seed-file path, so moving or deleting that file
          will prevent a later run from reading it.
        </p>
      </section>

      <section id="support-protection">
        <h2>Safeguards and recovery</h2>
        <h3>Read-only mode</h3>
        <p>
          Use a project’s menu or a collection’s lock control to prevent new
          writes from Eldgard while keeping reads and exports available.
          Inherited collection locks must be removed at the parent that enabled
          them. Requests already sent can finish. These app settings do not
          change Google IAM or Firebase Security Rules.
        </p>
        <h3>Single-document review</h3>
        <p>
          Review the exact environment, document path, typed diff, and loaded
          server version. If another client changed the document, reload the
          latest version, rebase the draft, and review again. An unknown write
          outcome also requires a reload; it is not proof that nothing changed.
          Bulk imports, collection operations, and multi-document saves have
          their own behavior and are not an atomic transaction.
        </p>
        <h3>Returning after a restart</h3>
        <p>
          Workspace recovery restores your working context. Document-draft
          retention is optional and off by default. Reconnect to the exact
          target, revalidate access, and review a recovered change before
          applying it. Clearing recovery does not delete Firestore data.
        </p>
        <h3>Optional local mutation history</h3>
        <p>
          Enable Record reviewed document changes from Project actions → Local
          mutation history if retaining sensitive document contents locally is
          appropriate. Eligible confirmed single-document operations can be
          reviewed for restore, subject to current server-version or existence
          checks. This is not a complete audit trail, database backup,
          point-in-time recovery service, or bulk undo. It does not cover
          external writers, imports, or collection operations.
        </p>
        <p>
          Google OAuth and service-account access still follows{' '}
          <a
            href="https://firebase.google.com/docs/firestore/use-rest-api#authentication_and_authorization"
            target="_blank"
            rel="noreferrer"
          >
            Google’s Firestore REST authorization rules
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          . Use appropriately limited IAM permissions for production work.
        </p>
      </section>

      <section id="support-mobile">
        <h2>Mobile reader</h2>
        <p>
          The Android and iOS app is a read-only companion and is also in
          development. It can browse and query Firestore but does not provide
          document mutation methods. If a cloud connection works on desktop but
          not mobile, check the selected Google account, mobile OAuth setup,
          required scopes, Firestore Native mode, and IAM permissions.
        </p>
        <p>
          Removing a saved local connection removes only its profile from the
          device. It does not stop the emulator or change any Firestore data.
        </p>
        <p>
          Search covers loaded documents, not unseen collection pages. Query
          Count is an explicit server request and can count all matches or
          respect the query’s limit; it is not the number of loaded rows.
          Document Changes compares successful observations from your own
          refreshes, not every intervening write. Neither feature is a live
          monitoring service.
        </p>
      </section>

      <section id="support-handoff">
        <h2>Take a saved query to mobile</h2>
        <ol>
          <li>
            On desktop, choose Export for mobile on a saved query. Review the
            payload and copy it using Copy handoff.
          </li>
          <li>
            Transfer the text using a channel you trust. On mobile, open Import
            saved query, paste it, and choose Review import.
          </li>
          <li>
            Choose or set up your own matching connection, then confirm the
            environment, project, database, and collection or collection-group
            scope.
          </li>
          <li>
            Load the query draft. Review or edit it, then choose Run or Save
            separately. Importing does not execute the query.
          </li>
        </ol>
        <p>
          Credentials, emulator addresses, and result documents are excluded,
          but query values can still contain sensitive data. There is no
          automatic sync. Mobile can export this format too; desktop currently
          exports it but does not import it. A phone needs its own reachable
          emulator address, not the desktop’s localhost address.
        </p>
      </section>

      <section id="support-security">
        <h2>Security reports</h2>
        <p>
          Report a suspected vulnerability privately to{' '}
          <a href="mailto:hello@alxnsv.io?subject=Eldgard%20security%20report">
            hello@alxnsv.io
          </a>{' '}
          with the subject “Eldgard security report.” Include the affected
          version, impact, and safe reproduction steps. Do not access other
          people’s data, disrupt a service, or include live credentials in the
          report. No public bug-bounty program is currently offered.
        </p>
      </section>
    </DocumentPage>
  );
}

function DataDeletionPage() {
  return (
    <DocumentPage
      current="data-deletion"
      eyebrow="Leave no ember unattended"
      title="Data & Deletion"
      introduction={
        <p>
          Disconnect Eldgard, remove its local records, and understand what must
          be deleted separately.
        </p>
      }
      toc={[
        { href: '#deletion-boundary', label: 'What deletion means' },
        { href: '#deletion-google', label: 'Google access' },
        { href: '#deletion-desktop', label: 'Desktop data' },
        { href: '#deletion-mobile', label: 'Mobile data' },
        { href: '#deletion-keys', label: 'Service-account keys' },
        { href: '#deletion-files', label: 'Cloud data and files' },
        { href: '#deletion-support', label: 'Website and support' },
      ]}
    >
      <section id="deletion-boundary">
        <h2>What deletion means in Eldgard</h2>
        <p>
          Eldgard does not create an alxnsv.io app account and does not keep a
          server-side copy of your Firestore database. Most deletion therefore
          happens on your device, in your Google Account, or in the Firestore
          project and files you control.
        </p>
        <p>
          Removing a connection tells Eldgard to forget that connection. It does
          not delete a Google Cloud project, revoke an IAM role, erase Firestore
          documents, stop an emulator, remove a service-account key from Google
          Cloud, or delete an exported file.
        </p>
      </section>

      <section id="deletion-google">
        <h2>Disconnect Google access</h2>
        <ol>
          <li>
            <strong>Desktop:</strong> remove each project associated with the
            Google account from Eldgard. The saved secure session is removed
            when no selected projects remain for that account.
          </li>
          <li>
            <strong>Mobile:</strong> open Account and choose{' '}
            <strong>Sign out</strong>.
          </li>
          <li>
            For an account-level revocation, open{' '}
            <a
              href="https://myaccount.google.com/connections"
              target="_blank"
              rel="noreferrer"
            >
              Google Account connections
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
            , choose Eldgard, and remove its access. Revocation invalidates the
            authorization at Google even if a local app record remains.
          </li>
        </ol>
        <p>
          Your organization’s administrator may separately need to remove IAM
          roles, OAuth grants, or managed-device data. Eldgard cannot do that on
          the organization’s behalf.
        </p>
      </section>

      <section id="deletion-desktop">
        <h2>Delete desktop app data</h2>
        <p>
          Before closing Eldgard, clear its secure local records in the app:
        </p>
        <ul>
          <li>
            Open <strong>Workspace recovery</strong> and choose{' '}
            <strong>Clear saved workspace</strong>. This clears both secure
            recovery snapshots and pauses capture for this session. Merely
            turning off document-draft retention removes drafts, not all
            workspace metadata.
          </li>
          <li>
            Open <strong>Project actions → Local mutation history</strong> for
            each recorded scope. Turn off{' '}
            <strong>Record reviewed document changes</strong> to delete that
            history and stop recording. <strong>Clear history</strong> removes
            existing records but leaves recording enabled. Both secure copies
            are scrubbed when removal succeeds.
          </li>
          <li>
            Resolve any storage error before treating a clearing action as
            complete, and disconnect Google separately as described above.
          </li>
        </ul>
        <p>
          Then close Eldgard so it cannot rewrite settings while you remove
          them. Saved queries, query history, and Autoflow records currently use
          a legacy internal directory named <code>.fireground</code> in your
          home folder:
        </p>
        <ul>
          <li>
            delete <code>~/.fireground/queries.json</code> to remove desktop
            saved queries and recent query history; and
          </li>
          <li>
            delete <code>~/.fireground/flows.json</code> to remove Autoflow
            definitions, seed-file paths, and stored run status.
          </li>
          <li>
            delete <code>~/.fireground/read_only.json</code> to remove saved
            project and collection protection settings. This resets those local
            locks if you use the app again; it does not change IAM.
          </li>
        </ul>
        <p>
          The activity log exists only in memory and is cleared when the app
          closes. Uninstalling the desktop app may not remove files in your home
          directory or credentials retained by the operating system, so remove
          the items above and disconnect Google separately when you need a full
          cleanup.
        </p>
      </section>

      <section id="deletion-mobile">
        <h2>Delete mobile app data</h2>
        <p>
          You can remove individual local-emulator profiles from the Local
          connections screen and delete individual saved queries from the Saved
          queries sheet. These actions do not change data at the endpoint.
        </p>
        <p>
          Remove pins, clear or remove recent places, and forget remembered
          cloud destinations from Home independently. These are navigation
          records, not Firestore documents. In-memory document-change
          observations are not retained after the app process ends.
        </p>
        <p>To remove all Eldgard mobile storage:</p>
        <ul>
          <li>
            On Android, clear Eldgard’s storage in system settings or uninstall
            the app.
          </li>
          <li>
            On iPhone or iPad, choose <strong>Delete App</strong>.{' '}
            <strong>Offload App</strong> can preserve application data.
          </li>
        </ul>
        <p>
          Mobile operating systems, Google’s sign-in SDK, device backups, and
          enterprise management can retain authentication or app data under
          their own settings. Sign out and revoke the Google connection before
          deleting the app when you want to end Google access as well.
        </p>
      </section>

      <section id="deletion-keys">
        <h2>Service-account keys</h2>
        <p>
          Removing a service-account connection from Eldgard closes the live
          client and releases the in-memory key data. Eldgard does not delete
          the original JSON file. Delete that file from its storage location and
          backups if you no longer need it.
        </p>
        <p>
          To invalidate the credential, disable or delete the corresponding key
          in Google Cloud IAM and rotate any credential that may have been
          exposed. Removing the local JSON file alone does not revoke the key at
          Google.
        </p>
      </section>

      <section id="deletion-files">
        <h2>Firestore data, exports, imports, and clipboard</h2>
        <p>
          Delete or restore Firestore documents using the controls and backups
          for the project that owns them. An Eldgard import writes to that
          target; uninstalling Eldgard cannot undo it. Likewise, you must delete
          exported JSON or CSV and source import files from the locations and
          backups you control.
        </p>
        <p>
          On mobile, copied paths, values, or links may remain in clipboard
          history or device synchronization. Replace or clear the clipboard and
          review device clipboard settings separately.
        </p>
      </section>

      <section id="deletion-support">
        <h2>Website and support data</h2>
        <p>
          The website has no Eldgard user account to delete. Hosting and
          security providers may retain limited request logs according to their
          operational settings.
        </p>
        <p>
          To leave the early-access list and request deletion of your signup,
          email <a href="mailto:hello@alxnsv.io?subject=Remove%20Eldgard%20waitlist%20request">hello@alxnsv.io</a>{' '}
          from the address you submitted. We will remove accessible copies from
          Formspree and our inbox unless retention is required for legal or
          security reasons. Provider backups and operational logs follow their
          retention settings.
        </p>
        <p>
          To request deletion of a support conversation, email{' '}
          <a href="mailto:hello@alxnsv.io?subject=Delete%20Eldgard%20support%20data">
            hello@alxnsv.io
          </a>{' '}
          from the address used for the conversation and identify the message
          thread. We will delete the accessible support copy unless retention is
          required for legal, fraud-prevention, dispute, or security reasons.
          Residual backup copies may remain until the email provider’s backup
          cycle completes.
        </p>
      </section>
    </DocumentPage>
  );
}

export function EldgardDocsPage() {
  return (
    <EldgardDocumentShell>
      <main id="content" className="eldgard-legal-main" tabIndex={-1}>
        <header className="eldgard-legal-hero eld-container">
          <DocumentNavigation />
          <div className="eldgard-legal-hero-grid">
            <div>
              <p className="eldgard-eyebrow">Eldgard / Field notes</p>
              <h1>Documents</h1>
            </div>
            <div className="eldgard-legal-intro">
              <div>
                <p>
                  Privacy, terms, support, and deletion guidance for the Eldgard
                  desktop workspace and mobile reader.
                </p>
              </div>
              <p>Effective and last updated {effectiveDate}</p>
            </div>
          </div>
        </header>

        <section
          className="eldgard-doc-index eld-container"
          aria-label="Document index"
        >
          {documentLinks.map((item, index) => (
            <article key={item.key}>
              <span aria-hidden="true">0{index + 1}</span>
              <div>
                <h2>{item.title}</h2>
                <p>{item.description}</p>
              </div>
              <a href={item.href} aria-label={`Read ${item.title}`}>
                Read <ArrowRight size={17} aria-hidden="true" />
              </a>
            </article>
          ))}
        </section>

        <section className="eldgard-doc-assurance eld-container">
          <div aria-hidden="true">
            <ShieldCheck size={25} strokeWidth={1.3} />
          </div>
          <div>
            <p className="eldgard-eyebrow">Built from the boundary inward</p>
            <h2>Direct connections. Explicit control.</h2>
            <p>
              These documents distinguish data on your device, data sent to the
              endpoint you choose, and the permissions governed by Google Cloud
              IAM.
            </p>
          </div>
        </section>

        <section className="eldgard-doc-contact eld-container">
          <div>
            <FileText size={22} strokeWidth={1.3} aria-hidden="true" />
            <div>
              <p className="eldgard-eyebrow">One more question?</p>
              <h2>Write to the developer.</h2>
            </div>
          </div>
          <div>
            <p>
              Questions about the product, these policies, or an early-access
              build go directly to the person building Eldgard.
            </p>
            <a href="mailto:hello@alxnsv.io?subject=Eldgard%20question">
              hello@alxnsv.io <ArrowUpRight size={16} aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>
    </EldgardDocumentShell>
  );
}

export function EldgardLegalPage({ document }: { document: EldgardDocument }) {
  switch (document) {
    case 'privacy':
      return <PrivacyPolicy />;
    case 'terms':
      return <TermsOfUse />;
    case 'support':
      return <SupportPage />;
    case 'data-deletion':
      return <DataDeletionPage />;
  }
}
